import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Megaphone, Wrench, FileText, DollarSign } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatCurrency } from "@/lib/cn";
import { Badge } from "@/components/Badge";
import { QueryError } from "@/components/QueryError";
import { TourLauncherButton } from "@/components/wizard/TourLauncherButton";

export function ResidentDashboardPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const { data: announcements = [], isError: announcementsError } = useQuery({
    queryKey: ["resident-announcements-top"],
    queryFn: async () => (await api.get("/announcements")).data,
  });
  const { data: permits = [], isError: permitsError } = useQuery({
    queryKey: ["my-works-permits"],
    queryFn: async () => (await api.get("/works-permits")).data,
  });
  const { data: maintenance = [], isError: maintenanceError } = useQuery({
    queryKey: ["my-maintenance"],
    queryFn: async () => (await api.get("/maintenance-requests")).data,
  });
  const { data: fees = [], isError: feesError } = useQuery({
    queryKey: ["my-fees"],
    queryFn: async () => (await api.get("/maintenance-fees")).data,
  });

  const { data: myUnit } = useQuery({
    queryKey: ["my-unit", user?.unitId],
    queryFn: async () => (await api.get<{ unitNumber: string; code: string | null }>(`/units/${user!.unitId}`)).data,
    enabled: !!user?.unitId,
  });

  const hasError = announcementsError || permitsError || maintenanceError || feesError;

  if (!user?.unitId) {
    return (
      <div className="space-y-4">
        <div data-tour="dashboard-welcome" className="flex items-start justify-between gap-4 flex-wrap">
          <h1 className="text-2xl font-bold">{t("resident.dashboard")}</h1>
          <TourLauncherButton />
        </div>
        <div className="card text-slate-600">{t("resident.noUnit")}</div>
      </div>
    );
  }

  // totalDue includes any applicable late charge on overdue fees
  const balance = (fees as any[])
    .filter((f) => f.status !== "PAID")
    .reduce((s, f) => s + (typeof f.totalDue === "number" ? f.totalDue : parseFloat(f.amount)), 0);

  return (
    <div className="space-y-6">
      <div data-tour="dashboard-welcome" className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">{t("resident.dashboard")}</h1>
          {myUnit && (
            <p className="text-sm text-slate-500 mt-1">
              {t("resident.unitLabel")} <span className="font-medium text-slate-700">{myUnit.unitNumber}</span>
              {myUnit.code && <span className="font-mono text-xs ml-2 px-1.5 py-0.5 rounded bg-slate-100">{myUnit.code}</span>}
            </p>
          )}
        </div>
        <TourLauncherButton />
      </div>

      {hasError && <QueryError />}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/resident/fees" className="card hover:shadow-md transition flex items-center justify-between">
          <div>
            <div className="text-sm text-slate-500">Saldo Pendiente</div>
            <div className="text-2xl font-bold mt-1">{formatCurrency(balance)}</div>
          </div>
          <DollarSign className="text-red-500" />
        </Link>
        <Link to="/resident/works-permit" className="card hover:shadow-md transition flex items-center justify-between">
          <div>
            <div className="text-sm text-slate-500">{t("worksPermits.title")}</div>
            <div className="text-2xl font-bold mt-1">{permits.length}</div>
          </div>
          <FileText className="text-blue-500" />
        </Link>
        <Link to="/resident/maintenance" className="card hover:shadow-md transition flex items-center justify-between">
          <div>
            <div className="text-sm text-slate-500">{t("maintenance.title")}</div>
            <div className="text-2xl font-bold mt-1">{maintenance.length}</div>
          </div>
          <Wrench className="text-orange-500" />
        </Link>
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Megaphone size={18} /> {t("resident.latestAnnouncements")}
          </h2>
          <Link to="/resident/announcements" className="text-sm text-primary hover:underline">
            {t("dashboard.viewAll")}
          </Link>
        </div>
        {announcements.length === 0 ? (
          <p className="text-sm text-slate-500">—</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {(announcements as any[]).slice(0, 5).map((a) => (
              <li key={a.id} className="py-2">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="info">{t(`announcements.cat.${a.category}`)}</Badge>
                  <span className="text-xs text-slate-500">{a.publishedAt && formatDate(a.publishedAt, i18n.language)}</span>
                </div>
                <div className="font-medium">{a.title}</div>
                <p className="text-sm text-slate-600 line-clamp-2">{a.content}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
